/**
 * Emit hosts for the `errors` analytics group — 1 function
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  DESKTOP_CAPTURE_ORIGINS,
  type DesktopCaptureOriginProp,
} from '@/models/analytics/features/errors';
import {
  ErrorSeverity,
  SegmentActionName,
  SegmentObjectName,
} from '@heidi/analytics/events';

/** The Rust `ERROR` payload as received — untrusted, so every field is unknown. */
export type TauriErrorEventPayload = {
  message?: unknown;
  error_code?: unknown;
  capture_origin?: unknown;
};

// A shape check, not `hasDesktopErrorMapping`: a new Rust code with no copy yet
// would collapse to 'unrecognised' exactly when it is the new failure mode.
// The shape (uppercase, digits, `_`) cannot carry a device or user name.
const ERROR_CODE_SHAPE = /^[A-Z][A-Z0-9_]{0,63}$/;
const isCaptureOrigin = (
  value: string
): value is (typeof DESKTOP_CAPTURE_ORIGINS)[number] =>
  (DESKTOP_CAPTURE_ORIGINS as readonly string[]).includes(value);

// Arrow consts, not function declarations: the emit-host census
// (`scripts/verify-scribe-emit-hosts.ts`) counts every top-level function here.
const boundedIdentifier = <T extends string>(
  value: unknown,
  isKnown: (value: string) => value is T
): T | 'absent' | 'unrecognised' => {
  if (value === undefined || value === null || value === '') return 'absent';
  return typeof value === 'string' && isKnown(value) ? value : 'unrecognised';
};

/**
 * `message` is never sent: Rust builds it from raw cpal / ScreenCaptureKit /
 * WASAPI error text, and some branches interpolate the capture device's name,
 * which routinely carries its owner's name. Only its length leaves the device.
 */
export function trackTauriErrorEvent(payload: TauriErrorEventPayload | undefined) {
  const error_code = boundedIdentifier(payload?.error_code, (code): code is string =>
    ERROR_CODE_SHAPE.test(code)
  );
  const capture_origin: DesktopCaptureOriginProp = boundedIdentifier(
    payload?.capture_origin,
    isCaptureOrigin
  );
  // Left undefined without a string, so "no message" stays distinct from "empty".
  const message_length =
    typeof payload?.message === 'string' ? payload.message.length : undefined;

  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_ERROR_EVENT,
      error_code,
      capture_origin,
      message_length,
    },
  });
  // error_viewed is on the Snowplow denylist (`snowplow-suppression.ts`), so
  // this reaches Amplitude and Datadog only.
  track({
    event_name: TrackingEventName.ErrorViewed,
    props: {
      surface: 'desktop_ipc',
      screen: 'desktop',
      error_type: 'unknown',
      error_code,
      error_message: 'tauri_error_event',
      capture_origin,
      message_length,
      severity: ErrorSeverity.WARNING,
    },
  });
}
